import React from "react";
import styles from "../../DynamicForm.module.scss";
import { renderStandardInput } from "./StandardInputRenderer";
import { createSerialKey, cleanValue } from "../../utils/formHelpers";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';

/**
 * Vertikale Kachel-Darstellung für Formularelemente
 */
const VerticalTileRepresentation = ({ items, step, stepIndex, itemIndex }) => {
    // Ähnlich wie TileRepresentation aber mit vertikalem Layout
    const handleTileClick = (e, item) => {
        const input = e.currentTarget.querySelector('input');
        if (!input || input.disabled) return;
        
        const isRadio = input.type === 'radio';
        const isCheckbox = input.type === 'checkbox';
        
        // Bei Radio sicherstellen, dass nur eines ausgewählt werden kann
        if (isRadio) {
            if (input.checked) {
                // Wenn bereits ausgewählt, nichts tun
                return;
            }
            
            // Setze den Radio-Button auf ausgewählt
            input.checked = true;
            
            // Entferne aktiven Status bei allen anderen Radio-Buttons in der Gruppe
            const allRadios = document.querySelectorAll(`input[type="radio"][name="${input.name}"]`);
            allRadios.forEach(radio => {
                const radioTile = radio.closest(`.${styles.verticalTile}`);
                if (radioTile && radio !== input) {
                    radioTile.classList.remove(styles.active);
                    radio.checked = false;
                }
            });
            
            // Diese Kachel als aktiv markieren
            e.currentTarget.classList.add(styles.active);
        } else if (isCheckbox) {
            // Checkbox toggle
            input.checked = !input.checked;
            
            // Toggle aktive Klasse
            if (input.checked) {
                e.currentTarget.classList.add(styles.active);
            } else {
                e.currentTarget.classList.remove(styles.active);
            }
        }
        
        // Manuelles Event auslösen
        const changeEvent = new Event('change', { bubbles: true });
        input.dispatchEvent(changeEvent);
    };
    
    // Items aufteilen
    const tileItems = items.filter(item => item.type === 9 || item.type === 10);
    const standardItems = items.filter(item => item.type !== 9 && item.type !== 10);
    
    // Gruppiere die Radio-Buttons nach Name
    const radioGroups = {};
    tileItems.forEach(item => {
        if (item.type === 10 && item.required) {
            const name = `multi-${stepIndex}-${itemIndex}`;
            if (!radioGroups[name]) {
                radioGroups[name] = true;
            }
        }
    });
    
    return (
        <>
            <div className={`${styles.verticalTileContainer}`}>
                {tileItems.map((item, idx) => {
                    const isFirstRequiredRadio = item.type === 10 && item.required && 
                        !tileItems.slice(0, idx).some(prevItem => 
                            prevItem.type === 10 && prevItem.required
                        );
                    
                    const cleanedLabel = cleanValue(item.label);
                    
                    return (
                    <div 
                        key={idx} 
                        className={`${styles.verticalTile} card mb-3 w-100 p-0 cursor-pointer ${item.defaultChecked ? styles.active : ''}`}
                        onClick={(e) => handleTileClick(e, item)}
                        tabIndex={0}
                        role={item.type === 10 ? "radio" : item.type === 9 ? "checkbox" : "button"}
                    >
                        <div className="card-body d-flex align-items-center p-3">
                            {/* Versteckter Input */}
                            {item.type === 9 || item.type === 10 ? (
                                <input
                                    className={styles.hiddenInput}
                                    type={item.type === 9 ? "checkbox" : "radio"}
                                    id={`${step.title}.${item.label}`}
                                    name={item.type === 10 ? `radio_group_${stepIndex}` : `${step.title}.${item.label}`}
                                    required={isFirstRequiredRadio || !!item.required}
                                    data-serial-key={createSerialKey(step, item.label, idx)}
                                    aria-invalid="false"
                                    value={item.value || item.label}
                                    defaultChecked={item.defaultChecked}
                                    onChange={(e) => {
                                        // Ähnliches onChange-Handling wie bei TileRepresentation
                                        if (item.type === 10) {
                                            const allTiles = e.target.closest(`.${styles.verticalTileContainer}`).querySelectorAll(`.${styles.verticalTile}`);
                                            allTiles.forEach(tile => {
                                                const tileRadio = tile.querySelector('input[type="radio"]');
                                                if (tileRadio && tileRadio.name === e.target.name) {
                                                    if (tileRadio === e.target) {
                                                        tile.classList.add(styles.active);
                                                    } else {
                                                        tile.classList.remove(styles.active);
                                                    }
                                                }
                                            });
                                        } else if (item.type === 9) {
                                            const tile = e.target.closest(`.${styles.verticalTile}`);
                                            if (e.target.checked) {
                                                tile.classList.add(styles.active);
                                            } else {
                                                tile.classList.remove(styles.active);
                                            }
                                        }
                                    }}
                                />
                            ) : null}
                            
                            {/* Bild links, falls vorhanden */}
                            {item.image && (
                                <div className="me-3">
                                    <img 
                                        src={item.image} 
                                        alt={cleanedLabel} 
                                        className={`${styles.verticalTileImage} rounded`}
                                    />
                                </div>
                            )}
                            
                            {/* Text-Content mit Silbentrennung */}
                            <div 
                                className={`${styles.verticalTileLabel} ${styles.tileText} flex-grow-1 text-center`}
                                lang="de"
                            >
                                <strong>{cleanedLabel}</strong>
                                {isFirstRequiredRadio === 1 && <span className="ms-1 text-danger">*</span>}
                                {item.description && <div className="form-text mt-1" lang="de">{item.description}</div>}
                            </div>
                            
                            {/* Check-Icon Container - korrigiert */}
                            <div className={styles.checkIconContainer}>
                                <FontAwesomeIcon 
                                    icon={faCheck} 
                                    className={styles.checkIcon}
                                    aria-hidden="true"
                                />
                            </div>
                        </div>
                    </div>
                    );
                })}
            </div>
            
            {/* Standard-Items für alle anderen Feld-Typen */}
            {standardItems.length > 0 && (
                <div className="mt-4">
                    {standardItems.map((item, idx) => {
                        // Add data-selection-item for SelectionCounter
                        const itemWithSelection = {
                            ...item,
                            "data-selection-item": "true"
                        };
                        
                        return renderStandardInput(itemWithSelection, step, idx, {
                            key: `standard-${idx}`,
                            wrapperClassName: "mb-3"
                        });
                    })}
                </div>
            )}
        </>
    );
};

export default VerticalTileRepresentation;
